import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { openCheckout, goToWhatsapp, PRODUCTS } from "../lib/payments";
import SectionHeading from "./SectionHeading";

const PROGRAMS = [
  {
    title: "12-Week Body Transformation",
    tagline: "For working professionals who want steady fat loss on real home food",
    badge: "MOST POPULAR",
    accent: "emerald",
    product: "physique-transformation",
    features: [
      "Low-GI, zero-sugar food plan with gram portions",
      "Daily meal-photo corrections on WhatsApp",
      "Full checkpoint review & re-tune every 15 days",
      "Sustainable walking & strength protocol",
      "Zero unnecessary supplements — evidence-based only",
    ],
  },
  {
    title: "Custom Nutrition & Dietetics Blueprint",
    tagline: "Food-first protocols for thyroid, PCOS, insulin resistance & gut health",
    badge: "HEALTH & PERFORMANCE",
    accent: "gold",
    product: "nutrition-blueprint",
    features: [
      "Protein prescribed in grams at every meal",
      "Indian home-food recipes & grocery list",
      "Gut health & inflammation protocol",
      "Blood-work-backed supplementation only if markers demand it",
    ],
  },
  {
    title: "Executive Lifestyle Protocol",
    tagline: "Tailored for busy working professionals with zero time to waste",
    badge: "CONCIERGE",
    accent: "emerald",
    product: "executive-protocol",
    features: [
      "Quick-prep meals built around your work hours",
      "Dining out, travel & function strategies",
      "Low-time-investment movement plan",
      "Direct WhatsApp access to Nikhil",
    ],
  },
];

export default function Programs() {
  const [paying, setPaying] = useState(null);
  const [paid, setPaid] = useState({});

  const enroll = async (product) => {
    setPaying(product);
    try {
      const res = await openCheckout({
        product,
        onSuccess: () => {
          setPaid((p) => ({ ...p, [product]: true }));
          toast.success(`Enrollment confirmed — ${PRODUCTS[product].label}. Now complete your application!`);
        },
      });
      if (res?.fallback) {
        toast.info("Opening WhatsApp — Nikhil will send you a secure payment link right away.");
        goToWhatsapp(product);
      }
    } catch (err) {
      toast.info("Opening WhatsApp — Nikhil will send you a secure payment link right away.");
      goToWhatsapp(product);
    } finally {
      setPaying(null);
    }
  };

  return (
    <section id="services" className="relative py-24 lg:py-32">
      <div
        aria-hidden="true"
        className="absolute top-1/3 -left-40 h-96 w-96 rounded-full bg-accent/8 blur-[120px]"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <SectionHeading
          eyebrow="Services"
          title="Choose Your Protocol"
          sub="Three focused coaching programs, all whole-food first — zero unnecessary supplements, nothing sold for commission. Enroll instantly with Razorpay's secure checkout; every application is still reviewed personally by Nikhil."
        />
        <div className="mt-14 grid lg:grid-cols-3 gap-6 items-stretch">
          {PROGRAMS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
              className={`group relative flex flex-col rounded-3xl border bg-panel p-8 lg:p-10 transition-all duration-300 hover:-translate-y-2 ${
                p.accent === "gold"
                  ? "border-gold/30 hover:border-gold/60 hover:shadow-glow-gold"
                  : "border-line hover:border-accent/60 hover:shadow-glow-emerald"
              }`}
              data-testid={`program-card-${i}`}
            >
              <span
                className={`absolute -top-3 left-8 px-3 py-1 rounded-full font-mono text-[10px] font-bold uppercase tracking-[0.2em] ${
                  p.accent === "gold" ? "bg-gold text-ink" : "bg-accent text-ink"
                }`}
              >
                {p.badge}
              </span>
              <h3 className="mt-4 font-display text-2xl font-extrabold uppercase leading-tight text-white">
                {p.title}
              </h3>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">{p.tagline}</p>
              <p className="mt-5 font-mono text-3xl font-bold text-white" data-testid={`program-price-${i}`}>
                ₹{PRODUCTS[p.product].price.toLocaleString("en-IN")}
                <span className="ml-2 text-xs text-slate-500 uppercase tracking-widest">full program</span>
              </p>
              <ul className="mt-7 space-y-3.5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-slate-200">
                    <span
                      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                        p.accent === "gold" ? "bg-gold/15 text-gold" : "bg-accent/15 text-emerald-400"
                      }`}
                    >
                      <Check className="h-3 w-3" />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              {paid[p.product] ? (
                <a
                  href="#intake-form"
                  data-testid={`program-enrolled-btn-${i}`}
                  className="mt-9 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-br from-accent to-accent-dim px-6 py-3.5 font-extrabold uppercase tracking-wide text-sm text-ink transition-all duration-300 hover:shadow-glow-emerald"
                >
                  <Check className="h-4 w-4" /> Paid — Complete Your Application
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => enroll(p.product)}
                  disabled={paying === p.product}
                  data-testid={`program-enroll-btn-${i}`}
                  className={`mt-9 inline-flex cursor-pointer items-center justify-center gap-2 rounded-full px-6 py-3.5 font-extrabold uppercase tracking-wide text-sm transition-all duration-300 disabled:opacity-70 ${
                    p.accent === "gold"
                      ? "border border-gold/50 text-gold group-hover:bg-gold group-hover:text-ink"
                      : "border border-accent/50 text-emerald-300 group-hover:bg-accent group-hover:text-ink"
                  }`}
                >
                  {paying === p.product ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ShieldCheck className="h-4 w-4" />
                  )}
                  Enroll Now · ₹{PRODUCTS[p.product].price.toLocaleString("en-IN")}
                </button>
              )}
              <a
                href="#intake-form"
                data-testid={`program-apply-link-${i}`}
                className="mt-4 text-center text-xs font-semibold text-slate-500 transition-colors hover:text-emerald-400"
              >
                Not ready? Apply free first
              </a>
            </motion.div>
          ))}
        </div>
        <p className="mt-8 flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-600">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
          Secure payments powered by Razorpay · UPI, Cards, NetBanking
        </p>
      </div>
    </section>
  );
}